"use client";

import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Card, CardBody } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { type AuthorChip, toAuthorChips } from "@/components/widget/author-avatars";
import { HelpListRow } from "@/components/widget/help-list-row";
import { StickySearchHeader } from "@/components/widget/sticky-search-header";
import { getMockHelpArticles, getMockHelpCategories, type MockHelpCategory } from "@/dummy";
import {
  useVisitorHelpFoldersQuery,
  useVisitorHelpListQuery,
  type VisitorHelpFoldersQuery,
  type VisitorHelpListQuery,
} from "@/lib/graphql/queries/generated/help.generated";
import { cacheKeys } from "@/lib/offline-cache";
import { isDemoReady, useDemoContent } from "@/lib/use-demo-translator";
import { useOfflineData } from "@/lib/use-offline-data";
import { useScrollRestore } from "@/lib/use-scroll-restore";
import { useWidgetInstance } from "@/lib/widget-instance-context";

interface HelpTabProps {
  isDummy: boolean;
  /** Drill into a collection. The parent already has the folder's
   *  display metadata (name / description / article count) from the
   *  list fetch — we thread it through so `HelpCategoryView` can
   *  paint the header without a second BE round-trip. */
  onOpenCategory: (
    categoryId: string,
    meta?: { name?: string; description?: string; articleCount?: number },
  ) => void;
  onOpenArticle: (slug: string) => void;
}

/**
 * Help **index** — sticky search at the top, total-count header
 * below, then a divider-separated list of collections. Drill-down
 * state lives in the shell so the bottom nav can hide on drill-ins;
 * this component just emits "open this category" / "open this
 * article" callbacks.
 *
 * Visual model is a familiar help center: each collection is
 * a full-width row (title + description + "Articles: N") with a
 * trailing chevron, separated by hairline dividers — no card chrome,
 * no per-row gaps.
 */
export function HelpTab({ isDummy, onOpenCategory, onOpenArticle }: HelpTabProps) {
  const t = useTranslations("help");
  const tDemo = useDemoContent();
  const locale = useLocale();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 300);
  // NOTE: HELP_ARTICLE open-tracking lives in `help-article-view` (keyed on
  // the article's `_id`), NOT here. The reaction bar submits `article.id`
  // (`_id`), so the tracked open must carry that SAME id for the backend to
  // match a later reaction to it — tracking the `slug` here would never match.
  // Tracking from the detail view also covers every open path (search, root,
  // category drill-down, related, home blocks) with one hook. See
  // `help-article-view/index.tsx`.

  const { data: folderData, loading: catLoading } = useVisitorHelpFoldersQuery({
    variables: { parentId: null, locale },
    skip: isDummy,
  });

  // Root-level articles — operators sometimes publish quick-reference
  // pages directly at the top of the help center rather than inside a
  // collection. `folderId: null` + `recursive: false` returns just
  // those (no subtree walk). Renders BELOW the collections list per
  // the visitor's mental model (browse by section first, fall through
  // to loose articles second). Skipped while searching — the search
  // results query covers everything globally already.
  const { data: rootArticlesData } = useVisitorHelpListQuery({
    variables: {
      args: { folderId: null, locale, recursive: false, limit: 20 },
    },
    skip: isDummy,
  });

  const { data: searchData } = useVisitorHelpListQuery({
    variables: { args: { query: debounced, locale, limit: 10 } },
    skip: debounced.length < 2 || isDummy,
  });

  // Offline READ fallback (public data): collections + root articles are
  // persisted so a fresh load with the BE down still shows the help center
  // instead of an empty screen. Search results aren't cached (transient).
  const helpPk = useWidgetInstance().publicKey;
  const offlineOn = !!helpPk && !isDummy;
  const effectiveFolderData = useOfflineData(
    offlineOn ? cacheKeys.helpCategories(helpPk as string, locale) : null,
    folderData,
  );
  const effectiveRootData = useOfflineData(
    offlineOn ? cacheKeys.helpRoot(helpPk as string, locale) : null,
    rootArticlesData,
  );

  const realFolders = useRealFolders(effectiveFolderData);
  const categories: MockHelpCategory[] = isDummy
    ? getMockHelpCategories(tDemo)
    : (realFolders ?? []);

  const isSearching = debounced.length >= 2;
  const realResults = useSearchResults(searchData);
  const dummyResults = isDummy
    ? getMockHelpArticles(tDemo)
        .filter(
          (a) =>
            a.title.toLowerCase().includes(debounced.toLowerCase()) ||
            a.excerpt.toLowerCase().includes(debounced.toLowerCase()),
        )
        .map<SearchResult>((a) => ({
          id: a.id,
          title: a.title,
          excerpt: a.excerpt,
          slug: a.slug,
          authors: toAuthorChips((a as { authors?: unknown }).authors),
        }))
    : [];
  const results: SearchResult[] = isDummy ? dummyResults : realResults;

  // Map the BE response to row data. Demo mode skips — the mocks
  // bucket every article into a category, no root-level orphans.
  const rootArticles: SearchResult[] = isDummy
    ? []
    : (effectiveRootData?.visitorHelpList?.items ?? []).map((a) => ({
        id: a._id,
        title: a.title,
        excerpt: a.excerpt ?? null,
        slug: a.slug,
        authors: toAuthorChips(a.authors),
      }));

  const viewportRef = useScrollRestore("help");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea followDirection className="min-h-0 flex-1" viewportRef={viewportRef}>
        <StickySearchHeader
          value={query}
          onChange={setQuery}
          placeholder={t("searchPlaceholder")}
        />

        {isSearching ? (
          <SearchResults results={results} onOpen={onOpenArticle} />
        ) : (isDummy && !isDemoReady(tDemo)) || (catLoading && categories.length === 0) ? (
          <ListSkeleton />
        ) : categories.length > 0 || rootArticles.length > 0 ? (
          <>
            {/* Total collections — quiet header line, no styling weight,
                just orients the visitor on the scope of the library. */}
            {categories.length > 0 && (
              <div className="border-wx-border border-b px-5 py-4">
                <p className="font-semibold text-base text-wx-fg">
                  {t("collectionsCount", { count: categories.length })}
                </p>
              </div>
            )}
            {/* Collections FIRST, root articles SECOND — all in one
                container so the `last:border-b-0` rule on `HelpListRow`
                only fires on the very last row of the combined list and
                the seam between sections keeps its divider. */}
            <div className="flex flex-col">
              {categories.map((cat, idx) => (
                <HelpListRow
                  key={cat.id}
                  title={cat.title}
                  description={cat.description}
                  meta={
                    typeof cat.articleCount === "number" && cat.articleCount > 0
                      ? t("articlesCount", { count: cat.articleCount })
                      : undefined
                  }
                  kind="folder"
                  onClick={() =>
                    onOpenCategory(cat.id, {
                      name: cat.title,
                      description: cat.description,
                      articleCount: cat.articleCount,
                    })
                  }
                  delay={idx * 0.03}
                />
              ))}
              {rootArticles.map((a, idx) => (
                <HelpListRow
                  key={a.id}
                  title={a.title}
                  description={a.excerpt}
                  authors={a.authors}
                  kind="article"
                  onClick={() => onOpenArticle(a.slug)}
                  delay={(categories.length + idx) * 0.03}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="px-5 py-6">
            <Card>
              <CardBody className="py-8 text-center">
                <p className="font-semibold text-sm text-wx-fg">{t("emptyTitle")}</p>
                <p className="mt-1 text-wx-fg-muted text-xs">{t("emptyBody")}</p>
              </CardBody>
            </Card>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

interface SearchResult {
  id: string;
  title: string;
  excerpt: string | null;
  slug: string;
  authors?: AuthorChip[];
  reactionCounts?: Record<string, number> | null;
}

function SearchResults({
  results,
  onOpen,
}: {
  results: SearchResult[];
  onOpen: (slug: string) => void;
}) {
  const t = useTranslations("help");
  if (results.length === 0) {
    return (
      <div className="px-5 py-6">
        <Card>
          <CardBody className="py-8 text-center">
            <p className="text-sm text-wx-fg-muted">{t("noResults")}</p>
          </CardBody>
        </Card>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      {results.map((r, idx) => (
        <HelpListRow
          key={r.id}
          title={r.title}
          description={r.excerpt}
          authors={r.authors}
          onClick={() => onOpen(r.slug)}
          delay={idx * 0.03}
        />
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
          key={idx}
          className="flex items-center gap-3 border-wx-border border-b px-5 py-4 last:border-b-0"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-32 rounded" />
            <Skeleton className="h-3 w-56 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
          </div>
          <Skeleton className="h-4 w-4 rounded" />
        </div>
      ))}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function useRealFolders(
  data: VisitorHelpFoldersQuery | null | undefined,
): MockHelpCategory[] | null {
  if (!data) return null;
  return (data.visitorHelpFolders ?? []).map((f) => ({
    id: f._id,
    title: f.name,
    description: f.description ?? "",
    articleCount: f.articleCount ?? 0,
  }));
}

function useSearchResults(data: VisitorHelpListQuery | null | undefined): SearchResult[] {
  if (!data) return [];
  return (data.visitorHelpList?.items ?? []).map((r) => ({
    id: r._id,
    title: r.title,
    excerpt: r.excerpt ?? null,
    slug: r.slug,
    authors: toAuthorChips(r.authors),
    reactionCounts: (r.reactionCounts as Record<string, number> | null | undefined) ?? null,
  }));
}
